import { useCallback, useEffect, useRef, useState } from "react";

import { randomId } from "@excalidraw/common";

import type {
  CustomToolbarTool,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";

import { applyAgentOperations, getBoardContext } from "./sceneOps";

import "./BoardAgent.scss";

import type { AgentOperations, Point } from "./sceneOps";

export const AI_AGENT_TOOL = "ai-agent";

const AGENT_URL = "/api/agent";
const TOKEN_KEY = "excalidraw-ai-agent-token";
const LOCAL_BOARD_KEY = "excalidraw-ai-agent-local-board";
const HISTORY_KEY_PREFIX = "excalidraw-ai-agent-history:";
const MAX_HISTORY = 50;

const PANEL_WIDTH = 340;
const PANEL_HEIGHT = 420;
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

const sparklesIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
  </svg>
);

export const AI_AGENT_TOOLBAR_TOOLS: readonly CustomToolbarTool[] = [
  {
    customType: AI_AGENT_TOOL,
    icon: sparklesIcon,
    title: "AI — click on the board and describe what to draw",
  },
];

type ChatMessage = {
  role: "user" | "agent" | "error";
  text: string;
};

/** collab room id, or a per-browser id for boards that aren't shared */
const getBoardId = () => {
  const match = window.location.hash.match(/^#room=([a-zA-Z0-9_-]+),/);
  if (match) {
    return match[1];
  }
  let localId = localStorage.getItem(LOCAL_BOARD_KEY);
  if (!localId) {
    localId = `local-${randomId()}`;
    localStorage.setItem(LOCAL_BOARD_KEY, localId);
  }
  return localId;
};

const loadHistory = (boardId: string): ChatMessage[] => {
  try {
    return JSON.parse(
      localStorage.getItem(HISTORY_KEY_PREFIX + boardId) || "[]",
    );
  } catch {
    return [];
  }
};

const saveHistory = (boardId: string, history: ChatMessage[]) => {
  localStorage.setItem(
    HISTORY_KEY_PREFIX + boardId,
    JSON.stringify(history.slice(-MAX_HISTORY)),
  );
};

type Anchor = { scene: Point; screen: Point };

type BoardFile = { name: string; size: number };

const formatSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

export const BoardAgent = ({
  excalidrawAPI,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI;
}) => {
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  // resolved when the chat opens (the room can change while the app runs)
  const [boardId, setBoardId] = useState("");
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [tokenInput, setTokenInput] = useState("");
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [files, setFiles] = useState<BoardFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // clicking the board with the AI tool opens the chat at that point
  useEffect(
    () =>
      excalidrawAPI.onPointerDown((activeTool, pointerDownState, event) => {
        if (
          activeTool.type !== "custom" ||
          activeTool.customType !== AI_AGENT_TOOL
        ) {
          return;
        }
        const nextBoardId = getBoardId();
        setBoardId(nextBoardId);
        setHistory(loadHistory(nextBoardId));
        setAnchor({
          scene: { ...pointerDownState.origin },
          screen: { x: event.clientX, y: event.clientY },
        });
      }),
    [excalidrawAPI],
  );

  useEffect(() => {
    if (anchor) {
      inputRef.current?.focus();
    }
  }, [anchor]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [history, busy]);

  const pushMessage = useCallback(
    (message: ChatMessage) => {
      setHistory((prev) => {
        const next = [...prev, message];
        saveHistory(boardId, next);
        return next;
      });
    },
    [boardId],
  );

  const close = () => setAnchor(null);

  /** authorized request to the board's agent API; forgets a rejected token */
  const agentFetch = useCallback(
    async (subpath: string, init: RequestInit = {}) => {
      const response = await fetch(
        `${AGENT_URL}/boards/${encodeURIComponent(boardId)}/${subpath}`,
        {
          ...init,
          headers: { ...init.headers, Authorization: `Bearer ${token}` },
        },
      );
      if (response.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        throw new Error("Токен больше не действует — введите новый");
      }
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || `Ошибка ${response.status}`);
      }
      return result;
    },
    [boardId, token],
  );

  // files uploaded to the board are shared by everyone using AI on it
  useEffect(() => {
    if (!anchor || !boardId || !token) {
      return;
    }
    agentFetch("files")
      .then((result) => setFiles(result.files))
      .catch(() => setFiles([]));
  }, [anchor, boardId, token, agentFetch]);

  const uploadFiles = async (fileList: FileList | File[]) => {
    const selected = Array.from(fileList);
    if (!selected.length || uploading) {
      return;
    }
    setUploading(true);
    try {
      for (const file of selected) {
        if (file.size > MAX_UPLOAD_BYTES) {
          pushMessage({
            role: "error",
            text: `${file.name}: файл больше ${formatSize(MAX_UPLOAD_BYTES)}`,
          });
          continue;
        }
        await agentFetch(`files?name=${encodeURIComponent(file.name)}`, {
          method: "POST",
          body: file,
        });
      }
      const result = await agentFetch("files");
      setFiles(result.files);
    } catch (error: any) {
      pushMessage({ role: "error", text: error.message || String(error) });
    } finally {
      setUploading(false);
    }
  };

  const removeFile = async (name: string) => {
    try {
      const result = await agentFetch(`files/${encodeURIComponent(name)}`, {
        method: "DELETE",
      });
      setFiles(result.files);
    } catch (error: any) {
      pushMessage({ role: "error", text: error.message || String(error) });
    }
  };

  const saveToken = async () => {
    const candidate = tokenInput.trim();
    if (!candidate) {
      return;
    }
    const response = await fetch(`${AGENT_URL}/auth`, {
      headers: { Authorization: `Bearer ${candidate}` },
    });
    if (!response.ok) {
      setTokenError("Неверный токен");
      return;
    }
    localStorage.setItem(TOKEN_KEY, candidate);
    setToken(candidate);
    setTokenInput("");
    setTokenError(null);
  };

  const send = async () => {
    const prompt = input.trim();
    if (!prompt || !anchor || busy || !token) {
      return;
    }
    const point = anchor.scene;
    setInput("");
    setBusy(true);
    pushMessage({ role: "user", text: prompt });

    try {
      const ops: AgentOperations = await agentFetch("messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          ...getBoardContext(excalidrawAPI, point),
        }),
      });
      applyAgentOperations(excalidrawAPI, ops, point);
      pushMessage({ role: "agent", text: ops.reply || "Готово" });
    } catch (error: any) {
      pushMessage({ role: "error", text: error.message || String(error) });
    } finally {
      setBusy(false);
      inputRef.current?.focus();
    }
  };

  if (!anchor) {
    return null;
  }

  // keep the panel inside the window, next to the click point
  const left = Math.min(
    Math.max(8, anchor.screen.x + 12),
    window.innerWidth - PANEL_WIDTH - 8,
  );
  const top = Math.min(
    Math.max(8, anchor.screen.y + 12),
    window.innerHeight - PANEL_HEIGHT - 8,
  );

  return (
    <>
      <div
        className="board-agent__marker"
        style={{ left: anchor.screen.x, top: anchor.screen.y }}
      />
      <div
        className={`board-agent${
          dragOver && token ? " board-agent--drag-over" : ""
        }`}
        style={{ left, top, width: PANEL_WIDTH, maxHeight: PANEL_HEIGHT }}
        onDragOver={(event) => {
          if (token && event.dataTransfer.types.includes("Files")) {
            event.preventDefault();
            event.stopPropagation();
            setDragOver(true);
          }
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          if (token && event.dataTransfer.files.length) {
            // don't let the editor insert dropped images onto the canvas
            event.preventDefault();
            event.stopPropagation();
            uploadFiles(event.dataTransfer.files);
          }
          setDragOver(false);
        }}
        onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          // keep editor shortcuts from firing while typing
          event.stopPropagation();
          if (event.key === "Escape") {
            close();
          }
        }}
      >
        <div className="board-agent__header">
          <span className="board-agent__title">
            {sparklesIcon}
            AI
          </span>
          <button
            className="board-agent__close"
            onClick={close}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {!token ? (
          <div className="board-agent__token">
            <div>Введите токен доступа к AI (один раз для этого браузера):</div>
            <input
              type="password"
              value={tokenInput}
              onChange={(event) => setTokenInput(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && saveToken()}
              autoFocus
            />
            {tokenError && (
              <div className="board-agent__error">{tokenError}</div>
            )}
            <button onClick={saveToken}>Сохранить</button>
          </div>
        ) : (
          <>
            <div className="board-agent__messages" ref={listRef}>
              {!history.length && (
                <div className="board-agent__hint">
                  Опишите, что нарисовать в этом месте. Например: «схема клиент
                  → API → база данных» или «сделай выделенное синим». Файлы (📎
                  или перетащите сюда) агент тоже увидит.
                </div>
              )}
              {history.map((message, index) => (
                <div
                  key={index}
                  className={`board-agent__message board-agent__message--${message.role}`}
                >
                  {message.text}
                </div>
              ))}
              {busy && (
                <div className="board-agent__message board-agent__message--agent board-agent__thinking">
                  Думаю…
                </div>
              )}
            </div>
            {(files.length > 0 || uploading) && (
              <div className="board-agent__files">
                {files.map((file) => (
                  <span
                    key={file.name}
                    className="board-agent__file"
                    title={`${file.name} — ${formatSize(file.size)}`}
                  >
                    <span className="board-agent__file-name">{file.name}</span>
                    <button
                      aria-label={`Удалить ${file.name}`}
                      onClick={() => removeFile(file.name)}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {uploading && (
                  <span className="board-agent__file board-agent__thinking">
                    Загрузка…
                  </span>
                )}
              </div>
            )}
            <div className="board-agent__composer">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(event) => {
                  if (event.target.files) {
                    uploadFiles(event.target.files);
                  }
                  event.target.value = "";
                }}
              />
              <button
                className="board-agent__attach"
                title="Загрузить файлы в контекст агента (или перетащите их сюда)"
                aria-label="Загрузить файлы"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                📎
              </button>
              <textarea
                ref={inputRef}
                value={input}
                rows={2}
                placeholder="Что сделать здесь?"
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    send();
                  }
                }}
              />
              <button onClick={send} disabled={busy || !input.trim()}>
                ↑
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};
